import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SearchableSelect.scss';

export interface SearchableOption {
  id: string;
  name: string;
  /** Extra text that should match a search but is not shown as the label (e.g. an ISO code). */
  keywords?: string | null;
  /**
   * Shorter text for the CLOSED control only; the list and the search keep `name`. Dial codes
   * use it so the box reads "+971" while the list still says "+971 UNITED ARAB EMIRATES" — the
   * full label wrapped onto two lines in a narrow column (SA feedback, 1 Nov 2026).
   */
  selectedLabel?: string | null;
}

interface Props {
  id: string;
  options: SearchableOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  /**
   * Hide the search box for lists shorter than this. Defaults to 0, so every dropdown is
   * searchable — consistent behaviour matters more than saving a row on a two-item list,
   * and it keeps the keyboard flow identical across a form.
   */
  searchThreshold?: number;
  ariaLabelledBy?: string;
}

/**
 * A select you can type into.
 *
 * Built rather than pulled in: the project has no select library, and adding one for a single
 * form would put a new runtime dependency in the bundle. Renders as a Bootstrap form-select so
 * it sits beside the app's native selects without restyling, and keeps a real listbox with
 * keyboard support rather than a div that only works with a mouse.
 */
export const SearchableSelect: React.FC<Props> = ({
  id, options, value, onChange, placeholder = 'Select…',
  disabled = false, isInvalid = false, searchThreshold = 0, ariaLabelledBy,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(() => options.find(o => o.id === value) || null, [options, value]);
  const showSearch = options.length >= searchThreshold;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      o.name.toLowerCase().includes(q) || (o.keywords || '').toLowerCase().includes(q));
  }, [options, query]);

  // Close on an outside click or Escape, so it behaves like every other menu in the app.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    setCursor(Math.max(0, filtered.findIndex(o => o.id === value)));
    if (showSearch) window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[cursor] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const pick = (optionId: string) => {
    onChange(optionId);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[cursor];
      if (opt) pick(opt.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const listId = `${id}-listbox`;

  return (
    <div className="searchable-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`form-select form-select-sm searchable-select__control text-start${isInvalid ? ' is-invalid' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={ariaLabelledBy}
      >
        <span className={selected ? '' : 'text-muted'} title={selected ? selected.name : undefined}>
          {selected ? (selected.selectedLabel ?? selected.name) : placeholder}
        </span>
      </button>

      {open && (
        <div className="searchable-select__menu">
          {showSearch && (
            <div className="searchable-select__search">
              <input
                ref={searchRef}
                type="text"
                className="form-control form-control-sm"
                placeholder="Type to search…"
                value={query}
                onChange={e => { setQuery(e.target.value); setCursor(0); }}
                onKeyDown={onKeyDown}
                aria-label="Search options"
              />
            </div>
          )}
          <ul className="searchable-select__list" role="listbox" id={listId} ref={listRef}>
            {filtered.length === 0 && (
              <li className="searchable-select__empty">No matches</li>
            )}
            {filtered.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={o.id === value}
                className={
                  'searchable-select__option'
                  + (i === cursor ? ' is-cursor' : '')
                  + (o.id === value ? ' is-selected' : '')
                }
                onMouseEnter={() => setCursor(i)}
                onMouseDown={e => { e.preventDefault(); pick(o.id); }}
              >
                {o.name}
                {o.keywords ? <span className="searchable-select__hint">{o.keywords}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
